import React from "react";
import { Info, Check } from "lucide-react";

const AuthenticationForm = ({
  codeDelivery,
  addSecurityRecommendation,
  addExpiryTime,
  expiryMinutes,
  onChange,
}) => {
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-gray-200 p-4 bg-white">
        <h3 className="text-sm font-bold text-gray-900 mb-0.5">
          Code delivery setup
        </h3>
        <p className="text-[11px] text-gray-500 mb-3">
          Choose how customers send the code from WhatsApp to your app. Edits to
          this section won't require review or count towards edit limits.
        </p>

        <div className="space-y-2">
          {/* Only Copy code is active for now */}
          <label
            className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
              codeDelivery === "copy_code"
                ? "border-[#048AAD] bg-[#048AAD]/5 ring-1 ring-[#048AAD]/20"
                : "border-gray-200 bg-white hover:bg-gray-50"
            }`}
          >
            <input
              type="radio"
              name="codeDelivery"
              value="copy_code"
              checked={codeDelivery === "copy_code"}
              onChange={(e) => onChange("codeDelivery", e.target.value)}
              className="mt-0.5 accent-[#048AAD]"
            />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-800 flex items-center gap-1">
                Copy code
                <Info size={11} className="text-gray-400" />
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">
                Basic authentication with quick setup. Your customers copy and
                paste the code into your app.
              </p>
            </div>
          </label>

          {/* Zero-tap and One-tap — commented out for now
          <label className="flex items-start gap-2.5 p-2.5 rounded-lg border border-gray-200 bg-white opacity-50 cursor-not-allowed">
            <input type="radio" name="codeDelivery" disabled className="mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-gray-400 flex items-center gap-1">
                Zero-tap auto-fill <Info size={11} className="text-gray-300" />
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5">
                Zero-tap will automatically send code without requiring your customer to tap a button.
              </p>
            </div>
          </label>

          <label className="flex items-start gap-2.5 p-2.5 rounded-lg border border-gray-200 bg-white opacity-50 cursor-not-allowed">
            <input type="radio" name="codeDelivery" disabled className="mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-gray-400 flex items-center gap-1">
                One-tap auto-fill <Info size={11} className="text-gray-300" />
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5">
                The code sends to your app when customers tap the button.
              </p>
            </div>
          </label>
          */}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 p-4 bg-white">
        <h3 className="text-sm font-bold text-gray-900 mb-0.5">Content</h3>
        <p className="text-[11px] text-gray-500 mb-3">
          Content for authentication message templates can't be edited. You can
          add additional content from the options below.
        </p>

        <div className="space-y-2.5">
          <label
            className="flex items-center gap-2.5 cursor-pointer group"
            onClick={(e) => {
              e.preventDefault();
              onChange("addSecurityRecommendation", !addSecurityRecommendation);
            }}
          >
            <div
              className={`w-[18px] h-[18px] rounded flex items-center justify-center border-[1.5px] transition-colors shrink-0 ${
                addSecurityRecommendation
                  ? "bg-[#048AAD] border-[#048AAD]"
                  : "border-gray-300 bg-white group-hover:border-gray-400"
              }`}
            >
              {addSecurityRecommendation && (
                <Check size={12} className="text-white" strokeWidth={3} />
              )}
            </div>
            <span className="text-sm text-gray-700">
              Add security recommendation
            </span>
          </label>

          <div>
            <label
              className="flex items-center gap-2.5 cursor-pointer group"
              onClick={(e) => {
                e.preventDefault();
                onChange("addExpiryTime", !addExpiryTime);
              }}
            >
              <div
                className={`w-[18px] h-[18px] rounded flex items-center justify-center border-[1.5px] transition-colors shrink-0 ${
                  addExpiryTime
                    ? "bg-[#048AAD] border-[#048AAD]"
                    : "border-gray-300 bg-white group-hover:border-gray-400"
                }`}
              >
                {addExpiryTime && (
                  <Check size={12} className="text-white" strokeWidth={3} />
                )}
              </div>
              <span className="text-sm text-gray-700">
                Add expiry time for the code
              </span>
            </label>

            {addExpiryTime && (
              <div className="mt-2 ml-7 flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-600">
                  Expires in
                </span>
                <input
                  type="number"
                  value={expiryMinutes}
                  onChange={(e) =>
                    onChange(
                      "expiryMinutes",
                      Math.max(1, parseInt(e.target.value, 10) || 1)
                    )
                  }
                  min={1}
                  max={90}
                  className="w-16 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD] text-center"
                />
                <span className="text-xs text-gray-500">minutes</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationForm;
